/**
 * Location seam: how the check-in / check-out flow obtains a position.
 *
 * The UI depends ONLY on the {@link LocationProvider} interface. The default is
 * a thin wrapper over the browser `navigator.geolocation` API, which works in
 * the Capacitor WebView for foreground one-shot fixes (and in the browser for
 * local dev).
 *
 * Continuous capture ({@link LocationTracker}) runs over the Sessions position
 * stream in `services/background-location.ts` — native background capture in
 * the iOS/Android shells while a session is active, `navigator.geolocation`
 * otherwise. The one-shot provider here stays browser-based.
 */

import type { GeoStamp } from '@shared/sessions/types'
import {
  geolocationDeniedMessage,
  geolocationTimeoutMessage,
  geolocationUnavailableMessage,
  queryGeolocationPermission,
  type GeolocationPermissionState,
} from './geolocation-permission'

export interface LocationProvider {
  /** Resolve a single best-effort position fix as a domain {@link GeoStamp}. */
  getCurrentPosition(): Promise<GeoStamp>
}

/**
 * Continuous-capture seam used by the check-in / check-out flow to record the
 * device's GPS track for billable-kilometre computation.
 *
 * "Capture" (recording device GPS while a session is in progress) is the only
 * concern here — it is purely an on-device responsibility. "Accuracy refinement"
 * (snap-to-roads, better geocoding) is a SEPARATE optional cloud concern and is
 * intentionally NOT part of this interface (see
 * `docs/sessions-location-tracking.md`).
 *
 * Implementations must be swappable. The default {@link WebLocationTracker}
 * subscribes to the shared position stream (`services/background-location.ts`):
 * the native BackgroundLocation plugin while a session is active in the
 * iOS/Android shells, the browser `watchPosition` otherwise.
 */
export interface LocationTracker extends LocationProvider {
  /**
   * Begin continuous capture. `onSample` is invoked for every position fix as
   * an ordered {@link GeoStamp}. Resolves once capture has started (or rejects
   * if it cannot start, e.g. unsupported/permission-denied). Calling this while
   * already tracking is a no-op for the underlying watcher.
   */
  startTracking(onSample: (sample: GeoStamp) => void): Promise<void>

  /**
   * Stop continuous capture and resolve with the full ordered sample track
   * captured since {@link startTracking}. Safe to call when not tracking
   * (resolves with an empty array).
   */
  stopTracking(): Promise<GeoStamp[]>
}

export class GeolocationError extends Error {
  constructor(
    message: string,
    readonly code: 'UNSUPPORTED' | 'PERMISSION_DENIED' | 'UNAVAILABLE' | 'TIMEOUT',
  ) {
    super(message)
    this.name = 'GeolocationError'
  }
}

/** Fast network/Wi-Fi fix — works on desktop browsers and indoors. */
const NETWORK_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 12_000,
  maximumAge: 60_000,
}

/** Precise GPS fix — slower; often unavailable on desktop dev machines. */
const GPS_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 30_000,
  maximumAge: 10_000,
}

/**
 * Map a browser `GeolocationPosition` to a domain {@link GeoStamp}. Shared by
 * the one-shot provider and the continuous {@link LocationTracker} so both
 * normalise coordinates identically.
 *
 * NOTE: the browser cannot report mock/fake fixes, so `isMock` is never set
 * here — the native BackgroundLocation plugin populates it for anti-tamper
 * validation in `shared/sessions/km.ts`.
 */
export function geoStampFromPosition(position: GeolocationPosition): GeoStamp {
  const { latitude, longitude, accuracy, speed } = position.coords
  return {
    lat: latitude,
    lng: longitude,
    timestamp: position.timestamp || Date.now(),
    accuracy: typeof accuracy === 'number' ? accuracy : undefined,
    speedMps: typeof speed === 'number' && speed >= 0 ? speed : undefined,
  }
}

/** Translate a browser `GeolocationPositionError` into a {@link GeolocationError}. */
export function toGeolocationError(
  error: GeolocationPositionError,
  permissionState?: GeolocationPermissionState,
): GeolocationError {
  // Desktop macOS often reports POSITION_UNAVAILABLE when system location is off or
  // the site is blocked — cross-check the Permissions API when we have it.
  if (permissionState === 'denied') {
    return new GeolocationError(geolocationDeniedMessage(), 'PERMISSION_DENIED')
  }

  switch (error.code) {
    case error.PERMISSION_DENIED:
      return new GeolocationError(geolocationDeniedMessage(), 'PERMISSION_DENIED')
    case error.POSITION_UNAVAILABLE:
      return new GeolocationError(geolocationUnavailableMessage(), 'UNAVAILABLE')
    case error.TIMEOUT:
      return new GeolocationError(geolocationTimeoutMessage(), 'TIMEOUT')
    default:
      return new GeolocationError(error.message || geolocationUnavailableMessage(), 'UNAVAILABLE')
  }
}

/** Non-prompting permission read — re-exported for the check-in UI. */
export { queryGeolocationPermission } from './geolocation-permission'
export type { GeolocationPermissionState } from './geolocation-permission'

function requestPosition(
  options: PositionOptions,
  permissionState?: GeolocationPermissionState,
): Promise<GeoStamp> {
  return new Promise<GeoStamp>((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new GeolocationError('Geolocation is not supported on this device.', 'UNSUPPORTED'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(geoStampFromPosition(position)),
      (error) => reject(toGeolocationError(error, permissionState)),
      options,
    )
  })
}

/** Default browser/WebView implementation using `navigator.geolocation`. */
export class BrowserLocationProvider implements LocationProvider {
  /** When set, skips the built-in network→GPS fallback (tests / overrides). */
  constructor(private readonly options?: PositionOptions) {}

  async getCurrentPosition(): Promise<GeoStamp> {
    if (this.options) {
      return requestPosition(this.options)
    }

    const permission = await queryGeolocationPermission()
    if (permission === 'denied') {
      throw new GeolocationError(geolocationDeniedMessage(), 'PERMISSION_DENIED')
    }

    // Desktop browsers and indoor Wi-Fi rarely satisfy a fresh high-accuracy GPS
    // fix within a short timeout. Try a fast network-based fix first, then fall
    // back to GPS for mobile/outdoor precision.
    try {
      return await requestPosition(NETWORK_OPTIONS, permission)
    } catch (first) {
      if (first instanceof GeolocationError && first.code === 'PERMISSION_DENIED') {
        throw first
      }
      try {
        return await requestPosition(GPS_OPTIONS, permission)
      } catch (second) {
        if (second instanceof GeolocationError) {
          const latestPermission = await queryGeolocationPermission()
          if (latestPermission === 'denied') {
            throw new GeolocationError(geolocationDeniedMessage(), 'PERMISSION_DENIED')
          }
          if (second.code === 'UNAVAILABLE' || second.code === 'TIMEOUT') {
            throw second
          }
        }
        throw second
      }
    }
  }
}

let instance: LocationProvider | null = null

/** The process-wide location provider singleton (lazily created). */
export function getLocationProvider(): LocationProvider {
  if (!instance) {
    instance = new BrowserLocationProvider()
  }
  return instance
}

/** Test/demo hook: override the active location provider. */
export function setLocationProvider(provider: LocationProvider): void {
  instance = provider
}
